import {
  Component,
  Inject,
  Input,
  OnInit,
  EventEmitter,
  Output,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormArray,
  Validators,
  ReactiveFormsModule,
  FormsModule,
  AbstractControl,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { catchError, Observable } from 'rxjs';
import { Inputfiledsbase } from '../../../inputfileds/models/inputfiledsbase';
import { CommonModule } from '@angular/common';
import { DynamicFormQuestionComponent } from '../../../inputfileds/dynamic-form-question/dynamic-form-question.component';
import { MatIconModule } from '@angular/material/icon';
import { BlobServiceClient } from '@azure/storage-blob';
import { MatButtonModule } from '@angular/material/button';
import { DynamicPageCreationComponent } from '../dynamic-page-creation/dynamic-page-creation.component';
import { ToastService } from '../../../Services/toast/toast.service';
import { MatInputModule } from '@angular/material/input';
import { ConnectiveservicesService } from '../../../Services/Apis/Connectiveservices/connectiveservices.service';
import { TranslateDirective } from '../../Utilites/Directives/translate.directive';

@Component({
  selector: 'app-dynamic-file-upload',
  imports: [
    MatDialogModule,
    ReactiveFormsModule,
    CommonModule,
    MatFormFieldModule,
    MatButtonModule,
    DynamicFormQuestionComponent,
    MatIconModule,
    FormsModule,
    MatInputModule,
    TranslateDirective
  ],
  templateUrl: './dynamic-file-upload.component.html',
  styleUrls: ['./dynamic-file-upload.component.scss'],
})
export class DynamicFileUploadComponent implements OnInit {
  @Input() allowedFileTypes: string = '.pdf,.docx,.xlsx';

  @Input() questions$!: Observable<Inputfiledsbase<string>[]>;
  @Output() cancel: EventEmitter<void> = new EventEmitter<void>();
  @Output() formSubmit: EventEmitter<any> = new EventEmitter<any>();

  questions: Inputfiledsbase<string>[] = [];
  uploadForm: FormGroup;
  selectedFiles: File[] = [];
  isDragging: boolean = false;
  levels: { key: string; id: string | number; value: number }[] = [];
  selectedLevel: string = '';
  customLevel: string = '';
  showCustomInput: boolean = false;
  isLink: boolean = false;
  service = this.data?.service;
  accessUrl: string = '';
  containerName: string = '';
  FolderDataPath: string = '';

  // Getter for the FormArray of links
  get linkList(): FormArray {
    return this.uploadForm.get('linkList') as FormArray;
  }

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<DynamicPageCreationComponent>,
    private toast: ToastService,
    private connectiveservices: ConnectiveservicesService
  ) {
    // Initialize the form with a FormArray for links for potential use
    this.uploadForm = this.fb.group({
      linkList: this.fb.array([]), // Initialize FormArray for multiple links
    });

    if (this.data?.questions$) {
      this.data.questions$
        .pipe(
          catchError((err: Error) => {
            console.error('Error loading questions:', err);
            return [];
          })
        )
        .subscribe((questions: any) => {
          this.questions = questions || [];
          // Add dynamic controls to the existing FormGroup
          this.addDynamicControls(this.questions);
        });
    }
  }

  ngOnInit(): void {
    this.isLink = this.data?.isLink || false;
    this.service = this.data?.service;
    this.accessUrl = this.data?.accessUrl;
    this.containerName = this.data?.containerName;
    this.FolderDataPath = this.data?.folderDataPath || '';
    this.loadLevels();

    if (this.isLink && this.linkList.length === 0) {
      this.addLinkInput();
    }
  }

  // Helper to create a single link FormGroup
  private createLinkGroup(): FormGroup {
    // A simple URL validator could be added here
    const urlPattern = /^(http|https):\/\/[^ "]+$/;
    return this.fb.group({
      link: ['', [Validators.required, Validators.pattern(urlPattern)]],
    });
  }

  // Method to add a new link input to the FormArray
  addLinkInput(): void {
    this.linkList.push(this.createLinkGroup());
  }

  // Method to remove a link input from the FormArray
  removeLinkInput(index: number): void {
    this.linkList.removeAt(index);
    // Ensure at least one input remains if the list is empty and we are in link mode
    if (this.isLink && this.linkList.length === 0) {
      this.addLinkInput();
    }
  }

  // Add dynamic form controls based on questions
  private addDynamicControls(questions: Inputfiledsbase<string>[]): void {
    questions.forEach((question) => {
      const validators = question.required ? [Validators.required] : [];
      this.uploadForm.addControl(
        question.key,
        this.fb.control(question.value || '', validators)
      );
    });
  }

  // File selection and drag-drop logic
  onFileSelected(event: any) {
    if (!event.target.files || event.target.files.length === 0) return;
    this.selectedFiles = [
      ...this.selectedFiles,
      ...(Array.from(event.target.files as FileList) as File[]),
    ];
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer?.files.length) {
      const newFiles: File[] = Array.from(event.dataTransfer.files) as File[];
      this.selectedFiles = [...this.selectedFiles, ...newFiles];
    }
    this.isDragging = false;
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    this.isDragging = false;
  }

  documentInputChange(event: any) {
    const files = event.target.files;
    if (files.length > 0) {
      const newFiles: File[] = Array.from(files) as File[];
      this.selectedFiles = [...this.selectedFiles, ...newFiles];
    }
  }

  removeSelectedFile(index: number, event: Event) {
    event.stopPropagation();
    this.selectedFiles.splice(index, 1);
  }

  // Azure upload for a single file
  async uploadFileToAzure(file: File, folderPath: string): Promise<string> {
    const blobServiceClient = new BlobServiceClient(this.accessUrl);
    const containerClient = blobServiceClient.getContainerClient(
      this.containerName
    );
    const blobPath = `${folderPath}/${file.name}`;
    const blockBlobClient = containerClient.getBlockBlobClient(blobPath);

    await blockBlobClient.uploadBrowserData(file, {
      blobHTTPHeaders: { blobContentType: file.type },
    });

    return blockBlobClient.url;
  }

  async onSubmit() {
    if (this.selectedFiles.length === 0 && !this.isLink) {
      console.error('❌ Form invalid.');
      this.toast?.showToast('danger', 'Please check the form for errors.');
      return;
    }
    const orgData = this.connectiveservices.get_selected_sub_organization();
    const folderPath = `${orgData.sub_organization_short_name}${this.FolderDataPath}`;

    if (this.isLink) {
      const rawLinks = this.linkList.controls.map((control) =>
        (control as FormGroup).get('link')?.value?.trim()
      );
      const linkValues = rawLinks.filter((link) => link);

      if (!linkValues.length) {
        console.error('❌ No links provided.');
        this.toast?.showToast('danger', 'Please enter at least one link.');
        return;
      }

      const payload = {
        links_list: linkValues,
        ...this.uploadForm.value,
      };

      this.formSubmit.emit(payload);
      this.dialogRef.close();
      return;
    }

    if (!this.selectedFiles.length) {
      console.error('❌ No files selected.');
      this.toast?.showToast(
        'danger',
        'Please select at least one file to upload.'
      );
      return;
    }

    const fileUrls: string[] = [];
    let uploadFailed = false;

    const uploadPromises = this.selectedFiles.map(async (file) => {
      try {
        const urlWithSAS = await this.uploadFileToAzure(file, folderPath);
        const cleanUrl = urlWithSAS.split('?')[0];
        const basePath =
          'https://dbraeiassetmanagement.blob.core.windows.net/dbraei-dev/dbralc/';
        const trimmedPath = cleanUrl.startsWith(basePath)
          ? cleanUrl.substring(basePath.length)
          : cleanUrl;
        return trimmedPath;
      } catch (err) {
        console.error(`❌ Error uploading file ${file.name}:`, err);
        this.toast?.showToast('danger', `Failed to upload ${file.name}`);
        uploadFailed = true;
        return null;
      }
    });

    const results = await Promise.all(uploadPromises);
    const successfullyUploadedUrls = results.filter(
      (url): url is string => url !== null
    );

    if (!successfullyUploadedUrls.length) {
      console.error('❌ No files uploaded successfully.');
      if (!uploadFailed) {
        this.toast?.showToast(
          'danger',
          'An unknown error occurred during upload.'
        );
      }
      return;
    }

    const payload = {
      files_list: successfullyUploadedUrls,
    };

    // Only emit the payload, do NOT call API here
    this.formSubmit.emit(payload);
    this.dialogRef.close();
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  // Track questions by key
  trackByQuestionKey(index: number, question: Inputfiledsbase<string>): string {
    return question.key;
  }

  // Track links by index
  trackByLinkIndex(index: number, control: AbstractControl): number {
    return index;
  }

  private loadLevels(): void {
    this.levels =
      this.data?.service?.extractLevels?.(this.data.service.apiResponse) || [];
  }
}
